import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import InternalNotifications from './InternalNotifications';
import PackageSearch from './PackageSearch';
import AppLogo from './AppLogo';
import '../styles/professional-theme.css';
import {
  LayoutDashboard,
  Package,
  Ship,
  Truck,
  Users,
  Settings,
  Search,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  TrendingUp,
  Megaphone,
  ScrollText,
  Shield,
  HelpCircle,
  Activity,
  CreditCard,
  AlertTriangle,
  Menu,
  X
} from 'lucide-react';

const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

const ProfessionalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > TABLET_BREAKPOINT);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleResize = useCallback(() => {
    const w = window.innerWidth;
    const mobile = w <= MOBILE_BREAKPOINT;
    setIsMobile(mobile);
    if (mobile) {
      setSidebarOpen(false);
      setMobileMenuOpen(false);
    } else if (w <= TABLET_BREAKPOINT) {
      setSidebarOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Cmd+K / Ctrl+K shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const role = user?.role;

  // Navigation groupée
  const navGroups = [
    {
      items: [
        { label: t('overviewMenu'), path: '/dashboard', icon: LayoutDashboard,
          roles: ['platform_admin', 'company_admin', 'country_admin', 'partner', 'local_partner', 'loader'] },
      ]
    },
    {
      label: t('operationsSection'),
      items: [
        { label: t('myPackagesMenu'), path: '/my-packages', icon: Package,
          roles: ['company_admin', 'country_admin', 'partner'],
          children: [
            { label: t('allPackages'), path: '/my-packages', childRoles: ['company_admin', 'country_admin', 'partner'] },
            { label: t('drafts'), path: '/my-packages/drafts', childRoles: ['company_admin', 'country_admin', 'partner'] },
          ]
        },
        { label: t('departuresMenu'), path: '/shipments', icon: Ship,
          roles: ['company_admin', 'country_admin'] },
        { label: t('departuresMenu'), path: '/my-shipments', icon: Ship,
          roles: ['partner'] },
        { label: t('deliveriesMenu'), path: '/local-deliveries', icon: Truck,
          roles: ['local_partner'] },
      ]
    },
    {
      label: t('analysisSection'),
      items: [
        { label: t('statisticsMenu'), path: '/analytics', icon: BarChart3,
          roles: ['company_admin', 'country_admin'] },
        { label: t('financeMenu'), path: '/finance', icon: TrendingUp,
          roles: ['company_admin', 'partner'],
          children: [
            { label: t('byShipment'), path: '/finance/shipments', childRoles: ['platform_admin', 'company_admin', 'super_admin'] },
            { label: t('annualView'), path: '/finance/annual', childRoles: ['platform_admin', 'company_admin', 'super_admin'] },
            { label: t('invoices'), path: '/finance/invoices', childRoles: ['platform_admin', 'company_admin', 'super_admin'] },
            { label: t('pricing'), path: '/finance/pricing', childRoles: ['platform_admin', 'company_admin', 'super_admin'] },
            { label: t('analysis'), path: '/finance/analysis', childRoles: ['platform_admin', 'company_admin', 'super_admin'] },
          ]
        },
      ]
    },
    {
      label: t('managementSection'),
      items: [
        { label: t('broadcastMenu'), path: '/broadcast', icon: Megaphone,
          roles: ['company_admin'] },
        { label: t('teamMenu'), path: '/team', icon: Users,
          roles: ['company_admin', 'country_admin'] },
        { label: t('planMenu'), path: '/subscriptions', icon: CreditCard,
          roles: ['platform_admin', 'company_admin', 'super_admin'] },
        { label: t('incidents'), path: '/incidents', icon: AlertTriangle,
          roles: ['company_admin', 'country_admin'] },
        { label: t('auditLogMenu'), path: '/audit-logs', icon: ScrollText,
          roles: ['company_admin'] },
      ]
    },
    {
      items: [
        { label: t('adminMenu'), path: '/platform-admin', icon: Shield,
          roles: ['platform_admin'] },
        { label: t('monitoringMenu'), path: '/monitoring', icon: Activity,
          roles: ['platform_admin'] },
        { label: t('settingsMenu'), path: '/settings', icon: Settings,
          roles: ['platform_admin', 'super_admin', 'company_admin', 'country_admin', 'partner', 'local_partner'] },
      ]
    },
  ];

  // Filtrer par role
  const filteredGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(role))
    }))
    .filter(group => group.items.length > 0);

  const isActive = (path) => location.pathname === path;
  const isParentActive = (item) => item.children && location.pathname.startsWith(item.path);

  const toggleMenu = (path) => {
    setExpandedMenus(prev => ({ ...prev, [path]: !prev[path] }));
  };

  // Auto-expand parent menu when a child route is active
  const isMenuExpanded = (item) => {
    if (expandedMenus[item.path] !== undefined) return expandedMenus[item.path];
    return isParentActive(item);
  };

  return (
    <div className="pro-layout">
      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div className="pro-sidebar-backdrop" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      {(() => {
        // On mobile, when menu is open, treat sidebar as fully expanded
        const expanded = sidebarOpen || mobileMenuOpen;
        return (
      <aside className={`pro-sidebar ${expanded ? '' : 'pro-sidebar--collapsed'} ${mobileMenuOpen ? 'pro-sidebar--mobile-open' : ''}`}>
        {/* Logo */}
        <div className="pro-sidebar-header">
          {(expanded || isMobile) && (
            <div className="pro-sidebar-logo">
              <div className="pro-sidebar-logo-icon">
                <AppLogo size={16} />
              </div>
              <span className="pro-sidebar-logo-text">FretManager</span>
            </div>
          )}
          {isMobile ? (
            <button
              className="pro-sidebar-toggle"
              onClick={() => setMobileMenuOpen(false)}
            >
              <X size={18} />
            </button>
          ) : (
            <button
              className="pro-sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="pro-sidebar-nav">
          {filteredGroups.map((group, gi) => (
            <div key={gi} className="pro-nav-group">
              {group.label && expanded && (
                <div className="pro-nav-group-label">{group.label}</div>
              )}
              {!group.label && gi > 0 && <div className="pro-nav-divider" />}
              {group.items.map((item) => {
                const Icon = item.icon;
                const hasChildren = item.children?.filter(c => !c.childRoles || c.childRoles.includes(role)).length > 0;
                const active = isActive(item.path) || isParentActive(item);
                const isExpanded = hasChildren && expanded && isMenuExpanded(item);
                const visibleChildren = item.children?.filter(c => !c.childRoles || c.childRoles.includes(role)) || [];

                return (
                  <div key={item.path}>
                    <button
                      onClick={() => {
                        if (hasChildren && expanded) {
                          toggleMenu(item.path);
                        } else {
                          navigate(hasChildren ? (item.children[0]?.path || item.path) : item.path);
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`pro-nav-item ${active ? 'pro-nav-item--active' : ''}`}
                      title={!expanded ? item.label : undefined}
                    >
                      <Icon size={18} />
                      {expanded && (
                        <>
                          <span className="flex-1 text-left">{item.label}</span>
                          {hasChildren && (
                            <ChevronDown
                              size={14}
                              className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          )}
                        </>
                      )}
                    </button>
                    {isExpanded && (
                      <div className="ml-6 mt-0.5 space-y-0.5">
                        {visibleChildren.map((child) => (
                          <button
                            key={child.path}
                            onClick={() => { navigate(child.path); setMobileMenuOpen(false); }}
                            className={`pro-nav-item pro-nav-item--sub ${isActive(child.path) ? 'pro-nav-item--active' : ''}`}
                            style={{ fontSize: '13px', padding: '6px 12px' }}
                          >
                            <span>{child.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="pro-sidebar-footer">
          {expanded ? (
            <div className="pro-sidebar-user">
              <div className="pro-sidebar-avatar">
                {user?.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                  />
                ) : (
                  user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'
                )}
              </div>
              <div className="pro-sidebar-user-info">
                <div className="pro-sidebar-user-name">
                  {user?.name || user?.email}
                </div>
                <div className="pro-sidebar-user-role">
                  {user?.role?.replace('_', ' ')}
                </div>
              </div>
              <button className="pro-sidebar-logout" onClick={handleLogout} title={t('logout')}>
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button className="pro-sidebar-logout pro-sidebar-logout--centered" onClick={handleLogout} title={t('logout')}>
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>
        );
      })()}

      {/* Main */}
      <div className={`pro-main ${sidebarOpen ? '' : 'pro-main--expanded'}`}>
        {/* Top Bar */}
        <header className="pro-topbar">
          <button
            className="pro-mobile-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu size={20} />
          </button>
          {isMobile && (
            <span className="pro-topbar-mobile-title">FretManager</span>
          )}
          <button
            className="pro-topbar-search"
            onClick={() => setSearchOpen(true)}
            type="button"
          >
            <Search size={16} className="pro-topbar-search-icon" />
            <span className="pro-topbar-search-placeholder">{t('search')}</span>
            <kbd className="pro-topbar-search-kbd">⌘K</kbd>
          </button>
          <div className="pro-topbar-actions">
            <button
              className="pro-topbar-btn pro-topbar-search-mobile"
              onClick={() => setSearchOpen(true)}
              title={t('search')}
            >
              <Search size={18} />
            </button>
            {!isMobile && (
              <button className="pro-topbar-btn" title={t('help')}>
                <HelpCircle size={18} />
              </button>
            )}
            <InternalNotifications />
          </div>
        </header>

        {/* Page Content */}
        <main className="pro-content">
          {children}
        </main>
      </div>

      <PackageSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

export default ProfessionalLayout;
